import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User } from 'oidc-client';
import AsyncStatus, { AsyncStatusEnum } from '../model/common/AsyncStatus';
import userManager from '../utils/userManager';

export interface UserState {
    loadStatus: AsyncStatus;
    signinRedirectStatus: AsyncStatus;
    signinRedirectCallbackStatus: AsyncStatus;
    user: User | null;
}

const initialState: UserState = {
    loadStatus: AsyncStatusEnum.IDLE,
    signinRedirectStatus: AsyncStatusEnum.IDLE,
    signinRedirectCallbackStatus: AsyncStatusEnum.IDLE,
    user: null
};

export const loadUser = createAsyncThunk('user/loadUser', async (_, thunkAPI) => {
    var user = await userManager.getUser();
    if (user) {
        if (!user.expired) {
            thunkAPI.dispatch(userSlice.actions.userFound(user));
        } else {
            thunkAPI.dispatch(userSlice.actions.userExpired());
        }
    } else {
        thunkAPI.dispatch(userSlice.actions.userNotFound());
    }
    return user;
});

export const signinRedirect = createAsyncThunk('user/signinRedirect', (payload: {path: string}) => {
    return userManager.signinRedirect({ state: payload.path });
});

export const signinRedirectCallback = createAsyncThunk('user/signinRedirectCallback', () => {
    return userManager.signinRedirectCallback();
});

export const remoteSignout = createAsyncThunk('user/remoteSignout', () => {
    return userManager.removeUser();
});

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
      userRefreshed: (state: UserState, action: PayloadAction<User>) => {
        return { ...state, user: action.payload}
      },
      userFound: (state: UserState, action: PayloadAction<User>) => {
        return { ...state, user: action.payload}
      },
      userNotFound: (state: UserState) => {
        return { ...state, user: null };
      },
      userExpired: (state: UserState) => {
        return { ...state, user: null };
      },
      userSignedOut: (state: UserState) => {
        userManager.removeUser();
        userManager.signoutRedirect();
        return { ...state, user: null };
      },
      silentRenewErrored: (state: UserState, action: PayloadAction<Error>) => {
        return { ...state, user: null };
      }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadUser.pending, (state: UserState) => {
                state.loadStatus = AsyncStatusEnum.LOADING;
            })
            .addCase(loadUser.fulfilled, (state: UserState) => {
                state.loadStatus = AsyncStatusEnum.SUCCEEDED;
            })
            .addCase(loadUser.rejected, (state: UserState) => {
                state.loadStatus = AsyncStatusEnum.FAILED;
            })
            .addCase(signinRedirect.pending, (state: UserState) => {
                state.signinRedirectStatus = AsyncStatusEnum.LOADING;
            })
            .addCase(signinRedirectCallback.pending, (state: UserState) => {
                state.signinRedirectCallbackStatus = AsyncStatusEnum.LOADING;
            })
            .addCase(signinRedirectCallback.fulfilled, (state: UserState, action: PayloadAction<User>) => {
                state.signinRedirectCallbackStatus = AsyncStatusEnum.SUCCEEDED;
                state.user = action.payload;
            })
            .addCase(signinRedirectCallback.rejected, (state: UserState) => {
                state.signinRedirectCallbackStatus = AsyncStatusEnum.FAILED;
            })
            .addCase(remoteSignout.fulfilled, (state: UserState) => {
                state.user = null;
            })
            .addDefaultCase(() => {});
    }
});

export default userSlice;
